/**
 * IL FOGLIO DI CONDIVIDI: il messaggio che parte e i canali per mandarlo.
 *
 * Lo usano il ticket aperto della Lista scommesse (Home/PiedeGiocata) e la
 * maschera della prenotazione (Schedina/Prenotazione). Quali canali ci sono
 * lo decide il backoffice (le regole di services/regoleStampa); qui si toglie
 * solo quello che su questo apparecchio non puo' funzionare (utils/condividi).
 *
 * TRE FORME, lo stesso contenuto:
 *   - 'foglio'  (telefono): sale dal basso sopra il ticket. Il messaggio che
 *     parte, l'interruttore degli importi se la regola lo lascia scegliere,
 *     i canali in griglia con il nome sotto ogni icona, la nota;
 *   - 'tendina' (computer): si apre sopra il tasto Condividi, una riga per
 *     canale con accanto come si apre («apre WhatsApp Web», «copia, poi
 *     incolla»). Il messaggio non si mostra: lo si vede dove si incolla;
 *   - 'dentro'  (prenotazione): la sola griglia, dentro la maschera, senza
 *     finestra.
 *
 * COSA FA
 *   - ALL'APERTURA prepara il collegamento «Gioca anche tu» (preparaLink) e
 *     poi l'immagine: il foglio del sistema (navigator.share) vuole il gesto
 *     della persona ancora «caldo», e un giro sulla rete fra il tocco e la
 *     condivisione lo raffredda. Mentre il collegamento arriva i canali
 *     aspettano: un messaggio partito un attimo prima uscirebbe senza;
 *   - un rifiuto del servizio non ferma niente: il messaggio parte senza
 *     collegamento e la riga dice perche' («Il collegamento non c'e': …»);
 *   - «Mostra posta e vincita» solo se la regola dice SCEGLIE_GIOCATORE, e
 *     parte spento. VISIBILI li mette sempre, NASCOSTI mai;
 *   - un tasto vero per ogni canale acceso, «Altro…» compreso. I canali che
 *     sono indirizzi (wa.me, mailto:…) sono collegamenti; una scheda nuova
 *     solo per i siti https (mailto:, sms: e viber: aprono un'applicazione,
 *     e il computer lascerebbe dietro una scheda vuota). Negli indirizzi il
 *     messaggio si ferma a 20 eventi; Copia, Signal e Altro… lo portano
 *     intero;
 *   - l'immagine: col dito si condivide come file, col mouse si scarica.
 *
 * COSA NON FA: non scrive il messaggio (lo da' chi apre, con `testo`) e non
 * decide quali canali sono accesi.
 */
import React, {useEffect, useId, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import i18next from 'i18next';
import {
  faWhatsapp, faTelegram, faSignalMessenger, faXTwitter, faFacebook, faViber,
} from '@fortawesome/free-brands-svg-icons';
import {
  faEnvelope, faCommentSms, faCopy, faImage, faEllipsis, faXmark, faDownload, faCircleNotch,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import IconaFa from '../common/IconaFa';
import useDialogo from './useDialogo';
import {
  canaliVisibili, indirizzo, perIndirizzo, apreNuovaScheda, signalCopiando,
  puntatoreGrosso as eDito, puoCondividere as cePiattaforma, copiaTesto, nomeCanale,
} from '../../utils/condividi';
import './Condividi.css';

const t = (chiave, valori) => i18next.t(chiave, valori);

/* Quanto si aspetta il collegamento prima di lasciar partire il messaggio
   senza: un servizio lento non deve tenere fermi i tasti. Se arriva dopo,
   si aggiunge lo stesso. */
const ATTESA_LINK_MS = 8000;

const ICONE = {
  whatsapp: faWhatsapp, telegram: faTelegram, signal: faSignalMessenger, email: faEnvelope,
  sms: faCommentSms, x: faXTwitter, facebook: faFacebook, viber: faViber,
  copia: faCopy, immagine: faImage, altro: faEllipsis,
};

/* I colori delle marche, sul bordo del tasto; gli altri sono grigi. */
const COLORI = {
  whatsapp: '#25d366', telegram: '#2aabee', signal: '#5b8cf5', facebook: '#1877f2',
  viber: '#7360f2',
};

const AZIONI = ['signal', 'copia', 'immagine', 'altro'];

/* Le icone di FontAwesome alte quanto il testo accanto. */
const Icona = ({icon}) => <IconaFa icon={icon} style={{width: '1em', height: '1em'}}/>;

/**
 * Manda un'immagine: col dito, dove il sistema sa condividere un file, dal
 * foglio del sistema; altrimenti si scarica. Torna 'condivisa', 'scaricata'
 * o 'no'. Va chiamata DENTRO il tocco: niente attese prima di share().
 */
export function mandaImmagine(blob, {nomeFile = 'giocata.png', testo, dito = eDito()} = {}) {
  if (!blob) return Promise.resolve('no');
  let file = null;
  try {
    file = typeof File === 'function' ? new File([blob], nomeFile, {type: 'image/png'}) : null;
  } catch (e) {
    file = null;
  }
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  if (dito && file && nav && typeof nav.canShare === 'function' && typeof nav.share === 'function'
      && nav.canShare({files: [file]})) {
    const dati = {files: [file]};
    if (testo) dati.text = testo;
    return nav.share(dati).then(() => 'condivisa', (e) => (e && e.name === 'AbortError' ? 'chiusa' : 'no'));
  }
  return Promise.resolve(scaricaFile(blob, nomeFile) ? 'scaricata' : 'no');
}

/** Scarica un file dal browser (collegamento temporaneo, poi tolto). */
export function scaricaFile(blob, nomeFile) {
  if (typeof document === 'undefined' || typeof URL === 'undefined'
      || typeof URL.createObjectURL !== 'function') return false;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeFile;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  try {
    a.click();
  } finally {
    a.remove();
    /* Tolto dopo: tolto subito, qualche browser interrompe lo scaricamento. */
    setTimeout(() => {
      try { URL.revokeObjectURL(url); } catch (e) { /* gia' tolto */ }
    }, 10000);
  }
  return true;
}

/**
 * @param forma            'foglio' | 'tendina' | 'dentro'
 * @param titolo           il titolo del foglio («Condividi la giocata»)
 * @param regole           le regole di chi condivide (servono i canali e gli importi)
 * @param importi          la regola degli importi, se non e' quella di `regole`
 *                         (la prenotazione porta sempre la sua proposta: VISIBILI)
 * @param testo            (importi, link) => il messaggio che parte
 * @param preparaLink      () => Promise<{link, codice} | {errore} | null>, o null
 * @param link, codice     il collegamento gia' pronto (la prenotazione ce l'ha)
 * @param preparaImmagine  (importi, codice) => Promise<Blob|null>, o null
 * @param nomeFile         il nome del PNG (mai il codice della giocata)
 * @param cosa             'giocata' | 'prenotazione', per i nomi letti a voce
 * @param oggetto          l'oggetto della posta
 * @param ancora           il tasto che apre la tendina (ref), per metterla sopra
 * @param onChiudi         chiude il foglio
 */
export default function FoglioCondividi({
  forma = 'foglio',
  titolo,
  regole,
  importi: regolaImporti,
  testo,
  preparaLink = null,
  link: linkDato = '',
  codice: codiceDato = '',
  preparaImmagine = null,
  nomeFile = 'giocata.png',
  cosa = 'giocata',
  oggetto = '',
  ancora = null,
  onChiudi,
}) {
  const r = regole || {};
  const regola = regolaImporti || r.importi || 'NASCOSTI';
  const [conImporti, setConImporti] = useState(regola === 'VISIBILI');
  const scegliImporti = regola === 'SCEGLIE_GIOCATORE';

  /* IL COLLEGAMENTO: 'niente' (non si chiede), 'arriva', 'pronto', 'manca'. */
  const [collegamento, setCollegamento] = useState(() => {
    if (preparaLink) return {stato: 'arriva', link: '', codice: ''};
    if (linkDato) return {stato: 'pronto', link: linkDato, codice: codiceDato};
    return {stato: 'niente', link: '', codice: codiceDato};
  });
  const [immagine, setImmagine] = useState({stato: 'niente', blob: null});
  /* La conferma di un momento («Copiato»): sparisce da sola. */
  const [annuncio, setAnnuncio] = useState(null);
  const [copiato, setCopiato] = useState(false);

  const dito = useMemo(() => eDito(), []);
  const sistema = useMemo(() => cePiattaforma(), []);
  const idTitolo = useId();
  const idImporti = useId();
  const scatola = useRef(null);
  const orologi = useRef([]);
  useEffect(() => () => orologi.current.forEach(clearTimeout), []);

  useDialogo(scatola, onChiudi, forma !== 'dentro');

  const dopo = (fn, ms) => { orologi.current.push(setTimeout(fn, ms)); };
  const avvisa = (testoAvviso, tono = 'ok') => {
    setAnnuncio({testo: testoAvviso, tono, quando: Date.now()});
  };
  useEffect(() => {
    if (!annuncio) return undefined;
    const via = setTimeout(() => setAnnuncio(null), 3500);
    return () => clearTimeout(via);
  }, [annuncio]);

  /* ALL'APERTURA: il collegamento, una volta. */
  useEffect(() => {
    if (!preparaLink) return undefined;
    let vivo = true;
    let arrivato = false;
    const scaduto = setTimeout(() => {
      if (vivo && !arrivato) {
        setCollegamento({stato: 'manca', link: '', codice: '',
          motivo: t('non è arrivato in tempo, il messaggio parte senza')});
      }
    }, ATTESA_LINK_MS);
    Promise.resolve()
      .then(() => preparaLink())
      .then((esito) => {
        arrivato = true;
        if (!vivo) return;
        if (esito && esito.link) {
          setCollegamento({stato: 'pronto', link: esito.link, codice: esito.codice || ''});
        } else {
          setCollegamento({stato: 'manca', link: '', codice: '',
            motivo: (esito && esito.errore) || t('il servizio non l’ha preparato')});
        }
      }, () => {
        arrivato = true;
        if (vivo) {
          setCollegamento({stato: 'manca', link: '', codice: '',
            motivo: t('il servizio non l’ha preparato')});
        }
      });
    return () => { vivo = false; clearTimeout(scaduto); };
    // Una volta per apertura: chi apre di nuovo, rimonta il foglio.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const linkPronto = collegamento.stato === 'pronto' ? collegamento.link : '';
  const codicePronto = collegamento.stato === 'pronto' ? collegamento.codice : codiceDato;
  const attesa = collegamento.stato === 'arriva';

  const visibili = canaliVisibili(r.canali, {
    /* Facebook vuole un collegamento: mentre arriva si tiene il suo posto,
       cosi' la griglia non salta. */
    haLink: !!linkPronto || attesa,
    puntatoreGrosso: dito,
    puoCondividere: sistema,
  }).filter((k) => k !== 'immagine' || !!preparaImmagine);
  const conImmagine = visibili.indexOf('immagine') !== -1;

  /* L'IMMAGINE, dopo il collegamento (porta il suo codice), e di nuovo se
     cambia l'interruttore degli importi. */
  useEffect(() => {
    if (!conImmagine || attesa) return undefined;
    let vivo = true;
    setImmagine({stato: 'arriva', blob: null});
    Promise.resolve()
      .then(() => preparaImmagine(conImporti, codicePronto))
      .then((blob) => { if (vivo) setImmagine(blob ? {stato: 'pronta', blob} : {stato: 'manca', blob: null}); },
        () => { if (vivo) setImmagine({stato: 'manca', blob: null}); });
    return () => { vivo = false; };
    // preparaImmagine cambia a ogni disegno di chi apre: conta cosa disegna.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conImmagine, attesa, conImporti, codicePronto]);

  const messaggio = typeof testo === 'function' ? testo(conImporti, linkPronto) : '';
  const perUrl = perIndirizzo(messaggio);
  const copiando = signalCopiando({puntatoreGrosso: dito, puoCondividere: sistema});

  // --- le azioni ---------------------------------------------------------

  const condividiTesto = () => {
    navigator.share({title: titolo || undefined, text: messaggio})
      .catch((e) => {
        if (!e || e.name !== 'AbortError') avvisa(t('Non si è potuto condividere: prova con «Copia».'), 'errore');
      });
  };

  const copia = async (perSignal) => {
    const fatto = await copiaTesto(messaggio);
    if (!fatto) {
      avvisa(t('Non si è potuto copiare il testo.'), 'errore');
      return;
    }
    setCopiato(true);
    dopo(() => setCopiato(false), 2000);
    avvisa(perSignal ? t('Copiato: ora incollalo in Signal.') : t('Copiato'));
  };

  const mandaFoto = () => {
    if (!immagine.blob) return;
    mandaImmagine(immagine.blob, {nomeFile, testo: messaggio, dito}).then((come) => {
      if (come === 'scaricata') avvisa(t('Immagine scaricata'));
      else if (come === 'no') avvisa(t('Non si è potuto preparare l’immagine.'), 'errore');
    });
  };

  const azione = (k) => {
    if (k === 'signal') return copiando ? copia(true) : condividiTesto();
    if (k === 'copia') return copia(false);
    if (k === 'immagine') return mandaFoto();
    if (k === 'altro') return condividiTesto();
    return undefined;
  };

  // --- i nomi ------------------------------------------------------------

  const etichetta = (k) => {
    if (k === 'copia') {
      if (copiato) return t('Copiato');
      return forma === 'tendina' ? t('Copia il testo') : t('Copia');
    }
    if (k === 'immagine') return dito ? t('Condividi foto') : t('Scarica l’immagine');
    return nomeCanale(k);
  };

  /* Il nome letto a voce dell'immagine: il file non ha un testo, il tasto si. */
  const nomeImmagine = cosa === 'prenotazione'
    ? (dito ? t('Condividi foto della prenotazione') : t('Scarica l’immagine della prenotazione'))
    : (dito ? t('Condividi foto della giocata') : t('Scarica l’immagine della giocata'));

  const comeSiApre = (k) => {
    switch (k) {
      case 'whatsapp': return t('apre WhatsApp Web');
      case 'telegram': return t('sceglie la chat');
      case 'signal': return copiando ? t('copia, poi incolla') : t('dal foglio del telefono');
      case 'email': return t('apre la posta');
      case 'sms': return t('apre i messaggi');
      case 'x': return t('apre X');
      case 'facebook': return t('apre Facebook');
      case 'viber': return t('apre Viber');
      case 'altro': return t('le applicazioni del computer');
      default: return '';
    }
  };

  // --- i tasti -----------------------------------------------------------

  const tasto = (k) => {
    const riga = forma === 'tendina';
    const classe = riga ? 'lvc-cond-riga' : 'lvc-cond-canale';
    const stile = COLORI[k] ? {'--lvc-cond-colore': COLORI[k]} : undefined;
    let simbolo = ICONE[k];
    if (k === 'immagine' && !dito) simbolo = faDownload;
    if (k === 'copia' && copiato) simbolo = faCheck;
    const dentro = riga ? (
      <>
        <span className="icona" aria-hidden="true"><Icona icon={simbolo}/></span>
        <span className="nome">{etichetta(k)}</span>
        {comeSiApre(k) ? <span className="come">{comeSiApre(k)}</span> : null}
      </>
    ) : (
      <>
        <span className="lvc-cond-tondo" aria-hidden="true"><Icona icon={simbolo}/></span>
        <span>{etichetta(k)}</span>
      </>
    );

    if (AZIONI.indexOf(k) === -1) {
      /* Mentre il collegamento arriva, al posto dell'indirizzo un tasto
         spento: lo stesso aspetto, e non si preme. */
      if (attesa) {
        return (
          <button key={k} type="button" disabled className={classe} style={stile}>{dentro}</button>
        );
      }
      const href = indirizzo(k, {testo: perUrl, link: linkPronto, oggetto});
      if (!href) return null;
      const fuori = apreNuovaScheda(href) ? {target: '_blank', rel: 'noopener noreferrer'} : {};
      return (
        <a key={k} href={href} className={classe} style={stile} {...fuori}>{dentro}</a>
      );
    }
    const spento = attesa || (k === 'immagine' && immagine.stato !== 'pronta');
    return (
      <button key={k} type="button" className={classe} style={stile} disabled={spento}
        aria-label={k === 'immagine' ? nomeImmagine : undefined}
        onClick={() => azione(k)}>
        {dentro}
      </button>
    );
  };

  // --- la riga di stato (aria-live) ---------------------------------------

  let stato = null;
  if (annuncio) {
    stato = {tono: annuncio.tono, testo: annuncio.testo,
      icona: annuncio.tono === 'errore' ? null : faCheck};
  } else if (collegamento.stato === 'arriva') {
    stato = {tono: '', testo: t('Preparo il collegamento «Gioca anche tu»…'), icona: faCircleNotch, gira: true};
  } else if (collegamento.stato === 'pronto' && preparaLink) {
    stato = {tono: 'ok', testo: t('Collegamento pronto'), icona: faCheck};
  } else if (collegamento.stato === 'manca') {
    stato = {tono: 'no', testo: t('Il collegamento non c’è: {{motivo}}', {motivo: collegamento.motivo})};
  }
  const rigaStato = (
    <div className={'lvc-cond-stato' + (stato && stato.tono ? ' lvc-cond-stato--' + stato.tono : '')}
      aria-live="polite">
      {stato ? (
        <>
          {stato.icona ? (
            <span aria-hidden="true" className={stato.gira ? 'lvc-cond-gira' : undefined}
              style={{display: 'inline-flex'}}>
              <Icona icon={stato.icona}/>
            </span>
          ) : null}
          <span>{stato.testo}</span>
        </>
      ) : null}
    </div>
  );

  const nota = (
    <p className="lvc-cond-nota">
      {t('Si vedono solo i canali accesi nel backoffice.')}
      {visibili.indexOf('signal') !== -1 ? ' ' + (copiando
        ? t('Per Signal il testo si copia: poi lo incolli nella chat.')
        : t('Signal non accetta testo da un collegamento: si apre dal foglio del telefono col testo già scritto.'))
        : ''}
      {' '}{t('Nel messaggio non c’è mai il nome del conto.')}
    </p>
  );

  // --- le tre forme -------------------------------------------------------

  if (forma === 'dentro') {
    return (
      <div className="lvc-cond-dentro">
        <p className="lvc-cond-etichetta">{titolo || t('Mandala con')}</p>
        <div className="lvc-cond-griglia">{visibili.map(tasto)}</div>
        {rigaStato}
      </div>
    );
  }

  const interruttore = scegliImporti ? (
    <button type="button" role="switch" aria-checked={conImporti}
      aria-labelledby={idImporti + '-s'} aria-describedby={idImporti + '-d'}
      className="lvc-cond-interruttore" onClick={() => setConImporti((v) => !v)}>
      <span className="lvc-cond-pista" aria-hidden="true"><span/></span>
      <span>
        <span className="sopra" id={idImporti + '-s'}>{t('Mostra posta e vincita')}</span>
        <span className="sotto" id={idImporti + '-d'}>
          {conImporti
            ? t('Acceso: chi riceve vede quanto hai giocato.')
            : t('Spento: nel messaggio solo eventi e quote.')}
        </span>
      </span>
    </button>
  ) : null;

  if (forma === 'tendina') {
    const azioni = visibili.filter((k) => k === 'copia' || k === 'immagine');
    const canali = visibili.filter((k) => k !== 'copia' && k !== 'immagine');
    return createPortal(
      <>
        <div className="lvc-cond-velo lvc-cond-velo--chiaro" onClick={onChiudi} aria-hidden="true"/>
        <TendinaAlSuoPosto ancora={ancora}>
          {(posto) => (
            <div ref={scatola} role="dialog" aria-modal="true" aria-labelledby={idTitolo}
              tabIndex={-1} className="lvc-cond-tendina" style={posto}
              onClick={(e) => e.stopPropagation()}>
              <div className="lvc-cond-tendina-testa">
                <h2 className="lvc-cond-etichetta" id={idTitolo}>{t('Manda con')}</h2>
                {scegliImporti ? (
                  <label className="lvc-cond-spunta">
                    <input type="checkbox" checked={conImporti}
                      onChange={(e) => setConImporti(e.target.checked)}/>
                    {t('con gli importi')}
                  </label>
                ) : null}
              </div>
              {rigaStato}
              {canali.map(tasto)}
              {canali.length && azioni.length ? <div className="lvc-cond-separa" aria-hidden="true"/> : null}
              {azioni.map(tasto)}
              {nota}
            </div>
          )}
        </TendinaAlSuoPosto>
      </>,
      document.body,
    );
  }

  return createPortal(
    <div className="lvc-cond-velo" onClick={onChiudi}>
      <div ref={scatola} role="dialog" aria-modal="true" aria-labelledby={idTitolo} tabIndex={-1}
        className="lvc-cond-foglio" onClick={(e) => e.stopPropagation()}>
        <div className="lvc-cond-maniglia" aria-hidden="true"/>
        <div className="lvc-cond-testa">
          <h2 className="lvc-cond-titolo" id={idTitolo}>{titolo || t('Condividi la giocata')}</h2>
          <button type="button" className="lvc-cond-chiudi" aria-label={t('Chiudi')} onClick={onChiudi}>
            <Icona icon={faXmark}/>
          </button>
        </div>
        <div className="lvc-cond-blocco">
          <p className="lvc-cond-etichetta">{t('Il messaggio che parte')}</p>
          <div className="lvc-cond-messaggio">{messaggio}</div>
          {rigaStato}
        </div>
        {interruttore}
        <div className="lvc-cond-griglia">{visibili.map(tasto)}</div>
        {nota}
      </div>
    </div>,
    document.body,
  );
}

/* LA TENDINA SOPRA IL SUO TASTO. Fissa sullo schermo (il ticket scorre e ha
   i bordi tagliati), messa sopra il tasto se c'e' posto, altrimenti sotto,
   e rimessa a posto se la finestra cambia o il ticket scorre. */
function TendinaAlSuoPosto({ancora, children}) {
  const [posto, setPosto] = useState({right: 16, bottom: 72, maxHeight: 480});
  useLayoutEffect(() => {
    const metti = () => {
      const a = ancora && ancora.current;
      if (!a || typeof a.getBoundingClientRect !== 'function') return;
      const r = a.getBoundingClientRect();
      const alto = window.innerHeight || 0;
      const destra = Math.max(8, (window.innerWidth || 0) - r.right);
      const sopra = r.top - 16;
      const sotto = alto - r.bottom - 16;
      if (sopra >= 320 || sopra >= sotto) {
        setPosto({right: destra, bottom: Math.max(8, alto - r.top + 8), maxHeight: Math.max(200, sopra)});
      } else {
        setPosto({right: destra, top: r.bottom + 8, maxHeight: Math.max(200, sotto)});
      }
    };
    metti();
    window.addEventListener('resize', metti);
    window.addEventListener('scroll', metti, true);
    return () => {
      window.removeEventListener('resize', metti);
      window.removeEventListener('scroll', metti, true);
    };
  }, [ancora]);
  return children(posto);
}
